import { axiosInstance } from "."; // Assuming you have a configured axiosInstance
import {
  CvEditorSession,
  ProceedToBasicsResponse,
  SessionStatusResponse,
} from "../pages/private/myArea/menus/assessments/CVEditor/types/cv-editor.types";

const API_BASE_URL = "/cv-editor/sessions";

// --- PHASE 1: Session Start & CV Processing ---

/**
 * Fetches the user's current active session, if one exists.
 * @returns The session object or null.
 */
export const getCurrentSession = async (): Promise<CvEditorSession | null> => {
  try {
    const response = await axiosInstance.get<CvEditorSession>(
      `${API_BASE_URL}/current`
    );
    // An empty response body from NestJS can be an empty string, so check for ID.
    return response.data && response.data.id ? response.data : null;
  } catch (error) {
    console.error("Error fetching current session:", error);
    return null;
  }
};

/**
 * Creates a new, empty session on the backend.
 * @returns The newly created session with status 'AWAITING_CV_ACTION'.
 */
export const initiateSession = async (): Promise<CvEditorSession> => {
  const response = await axiosInstance.post<CvEditorSession>(
    `${API_BASE_URL}/initiate`
  );
  return response.data;
};

/**
 * Uploads a CV file to the specified session, triggering the parsing jobs.
 * @param sessionId The ID of the session to upload to.
 * @param cvFile The CV file from an input element.
 * @returns The updated session with status 'CV_PROCESSING'.
 */
export const uploadCv = async (
  sessionId: number,
  cvFile: File
): Promise<CvEditorSession> => {
  const formData = new FormData();
  formData.append("cvFile", cvFile);

  const response = await axiosInstance.post<CvEditorSession>(
    `${API_BASE_URL}/${sessionId}/upload-cv`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

/**
 * Tells the backend the user is ready to move past the initial screen.
 * The backend will respond indicating if the basics data is ready or if polling is needed.
 * @param sessionId The ID of the current session.
 * @returns An object containing the session status and readiness flags.
 */

interface ProceedToBasicsPayload {
  fromScratch?: boolean;
}

export const proceedToBasics = async (
  sessionId: number,
  payload: ProceedToBasicsPayload
): Promise<ProceedToBasicsResponse> => {
  const response = await axiosInstance.post<ProceedToBasicsResponse>(
    `${API_BASE_URL}/${sessionId}/proceed-to-basics`,
    payload
  );
  return response.data;
};

// --- PHASE 2: Data Validation & Targeting ---

/**
 * Submits the user-validated basic information.
 * @param sessionId The ID of the current session.
 * @param basicsData The user's confirmed basic info.
 * @returns An object indicating if the positions data is ready or if polling is needed.
 */
export const confirmBasics = async (
  sessionId: number,
  basicsData: any
): Promise<ProceedToBasicsResponse> => {
  const response = await axiosInstance.post<ProceedToBasicsResponse>(
    `${API_BASE_URL}/${sessionId}/confirm-basics`,
    { basics: basicsData }
  );
  return response.data;
};

/**
 * Submits the user-validated work positions.
 * @param sessionId The ID of the current session.
 * @param positionsData The user's confirmed list of positions.
 * @returns The updated session with status 'AWAITING_TARGET_ROLE'.
 */
export const confirmPositions = async (
  sessionId: number,
  positionsData: any[]
): Promise<CvEditorSession> => {
  const response = await axiosInstance.post<CvEditorSession>(
    `${API_BASE_URL}/${sessionId}/confirm-positions`,
    { positions: positionsData }
  );
  return response.data;
};

/**
 * Submits the user's target role.
 * @param sessionId The ID of the current session.
 * @param targetRole The role the user is applying for.
 * @returns The updated session with status 'INTERVIEW_LOADING'.
 */
export const confirmTargetRole = async (
  sessionId: number,
  targetRole: string
): Promise<CvEditorSession> => {
  const response = await axiosInstance.post<CvEditorSession>(
    `${API_BASE_URL}/${sessionId}/confirm-target-role`,
    { targetRole }
  );
  return response.data;
};

// --- PHASE 3: Interviews ---

/**
 * Sends a user's text reply during an interview.
 * @param sessionId The ID of the current session.
 * @param text The user's message.
 * @returns The updated session with the full conversation turn.
 */
export const sendInterviewTextReply = async (
  sessionId: number,
  text: string
): Promise<CvEditorSession> => {
  const response = await axiosInstance.post<CvEditorSession>(
    `${API_BASE_URL}/${sessionId}/interview-reply/text`,
    { text }
  );
  return response.data;
};

/**
 * Sends a user's audio reply during an interview.
 * @param sessionId The ID of the current session.
 * @param audioBlob The recorded audio data.
 * @param audioDuration The duration of the audio in seconds.
 * @returns The updated session with the full conversation turn.
 */
export const sendInterviewAudioReply = async (
  sessionId: number,
  audioBlob: Blob,
  audioDuration: number
): Promise<CvEditorSession> => {
  const formData = new FormData();
  formData.append("audioFile", audioBlob, `interview_audio_${Date.now()}.webm`);
  formData.append("audioDuration", String(audioDuration));

  const response = await axiosInstance.post<CvEditorSession>(
    `${API_BASE_URL}/${sessionId}/interview-reply/audio`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

/**
 * Tells the backend to transition to the next interview step (next position, or About Me, or Finalization).
 * @param sessionId The ID of the current session.
 * @returns The updated session in its new state.
 */
export const moveToNextInterviewStep = async (
  sessionId: number
): Promise<CvEditorSession> => {
  const response = await axiosInstance.post<CvEditorSession>(
    `${API_BASE_URL}/${sessionId}/next-interview-step`
  );
  return response.data;
};

// --- UTILITY & GENERAL ---

/**
 * Polls the backend for the current status of a session.
 * @param sessionId The ID of the current session.
 * @returns A lightweight session status object.
 */
export const getSessionStatus = async (
  sessionId: number
): Promise<SessionStatusResponse> => {
  const response = await axiosInstance.get<SessionStatusResponse>(
    `${API_BASE_URL}/${sessionId}/status`
  );
  return response.data;
};

/**
 * Archives a session, effectively a soft-delete.
 * @param sessionId The ID of the session to archive.
 */
export const archiveSession = async (sessionId: number): Promise<void> => {
  await axiosInstance.post(`${API_BASE_URL}/${sessionId}/archive`);
};

/**
 * Fetches the user's entire history of CV editor sessions.
 * @returns An array of session objects.
 */
export const fetchSessionHistory = async (): Promise<CvEditorSession[]> => {
  try {
    const response = await axiosInstance.get<CvEditorSession[]>(
      `${API_BASE_URL}/history`
    );
    return response.data || [];
  } catch (error) {
    console.error("Error fetching session history:", error);
    // Return an empty array or re-throw the error depending on how you want to handle it
    return [];
  }
};

export const sendInterviewSkipReply = async (
  sessionId: number
): Promise<CvEditorSession> => {
  const response = await axiosInstance.post<CvEditorSession>(
    `${API_BASE_URL}/${sessionId}/interview-reply/skip`
  );
  return response.data;
};
